import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { GRID_SCROLL_POSITION, GRID_CELL_DATA_QA, RESULTS_PER_PAGE } from 'search/enums/constants';
import { isSafari } from 'shared/utils/browserDetector';

interface IScrollData {
  page: number;
  pageHeight: number | string;
  path: string;
  productSelector: string;
  scrollTop: number;
}

interface IUsePersistGridScrollPosition {
  setSize: (page: number) => Promise<any[]>;
}

export const usePersistGridScrollPosition = ({ setSize }: IUsePersistGridScrollPosition) => {
  const router = useRouter();

  useEffect(() => {
    const scrollData = sessionStorage.getItem(GRID_SCROLL_POSITION);
    const prevPath = sessionStorage.getItem('prevPath');

    if (scrollData) {
      const { page, pageHeight, path, productSelector, scrollTop }: IScrollData =
        JSON.parse(scrollData);

      // check if navigating back to the grids page
      if (prevPath !== path) {
        return;
      }

      if (page > 1) {
        const origBodyHeight = document.body.style.height;
        document.body.style.height = `${pageHeight}px`;

        const restorePage = (shouldScroll = true) => {
          if (shouldScroll) window.scrollTo(0, scrollTop);
          document.body.style.height = origBodyHeight;
          window.history.scrollRestoration = 'auto';
        };

        setSize(page)
          .then(() => {
            const waitThenScroll = (retries = 0) => {
              window.requestAnimationFrame(() => {
                if (document.querySelector(productSelector)) {
                  restorePage();
                } else if (retries < 100) {
                  waitThenScroll(retries + 1);
                } else {
                  restorePage(false);
                }
              });
            };
            waitThenScroll();
          })
          .catch(() => {
            restorePage(false);
          });
      }
    }
  }, []);

  const handleBeforeUnload = () => {
    let productNum = 0;
    let productSelector;

    const products = [...document.querySelectorAll(`[data-qa="${GRID_CELL_DATA_QA}"]`)];
    const viewportHeight = window.innerHeight;

    for (let i = products.length - 1; i >= 0; i -= 1) {
      const product = products[i];
      if (!(product instanceof HTMLElement)) return;

      const { bottom, top } = product.getBoundingClientRect();

      // If the top or bottom of a product are in view, stop the check
      if ((top >= 0 && top <= viewportHeight) || (bottom >= 0 && bottom <= viewportHeight)) {
        productNum = +product.dataset.gridCellPosition;
        const productName = product.dataset.gridCellName;
        productSelector = `[data-grid-cell-name="${productName}"]`;
        break;
      }
    }

    const page = Math.ceil(productNum / RESULTS_PER_PAGE) || 0;

    const scrollData = {
      pageHeight: document.documentElement.scrollHeight || 'auto',
      page,
      productSelector: productSelector || '',
      scrollTop: window.scrollY || 0,
      path: router.asPath,
    };

    sessionStorage.setItem(GRID_SCROLL_POSITION, JSON.stringify(scrollData));

    if (page > 1 && !isSafari()) window.history.scrollRestoration = 'manual';
  };

  useEffect(() => {
    router.events.on('routeChangeStart', handleBeforeUnload);

    return () => {
      router.events.off('routeChangeStart', handleBeforeUnload);
    };
  }, []);
};
